// Helpers for creating XML nodes used across our AI prompts.

import type { PublicUserDataT } from '../users';
import { type XmlNodeT, xmlNode } from '../xml';

import {
  DEF_EMOTIONAL_MECHANISMS,
  DEF_INTENDED_SHIFT,
  DEF_MODALITIES,
  DEF_NEUROLOGICAL_MECHANISMS,
  DEF_PHYSICAL_MECHANISMS,
} from './definitions';

import {
  LIFE_MOMENT_IDS,
  LIFE_MOMENTS,
  type LifeMomentIdT,
} from './life-moments';

import type { DifficultyT } from './types';

export function xmlAuthor(
  user: PublicUserDataT | undefined,
): XmlNodeT<'author'> | null {
  return user ? xmlNode('author', `${user.firstName} ${user.lastName}`) : null;
}

export function xmlDifficulty(
  level: DifficultyT | DifficultyT[] | null | undefined,
): XmlNodeT<'difficulty'> | null {
  return xmlNode(
    'difficulty',
    Array.isArray(level) ? level.join('; ') : level,
  );
}

export function xmlDuration(
  minutes: null | number | undefined,
): XmlNodeT<'duration'> | null {
  return typeof minutes === 'number'
    ? xmlNode('duration', `${minutes} minutes`)
    : null;
}

export function xmlEmotionalMechanisms(
  mechanisms: string[] | undefined,
): XmlNodeT<'emotional_mechanisms'> | null {
  return xmlNode(
    'emotional_mechanisms',
    mechanisms?.join('; '),
    DEF_EMOTIONAL_MECHANISMS,
  );
}

export function xmlEnvironmentNeeds(
  environment: null | string | undefined,
): XmlNodeT<'environment_needs'> | null {
  return xmlNode('environment_needs', environment);
}

export function xmlEquipmentNeeds(equipment: string[]): XmlNodeT<'equipment_needs'> {
  return xmlNode('equipment_needs', equipment.join('; '));
}

export function xmlIntendedShift(
  from: string[] | undefined,
  to: string[] | undefined,
): XmlNodeT<'intended_person_state_shift'> | null {
  return xmlNode('intended_person_state_shift', [
    xmlNode('from', from?.join('; '), 'The state before the exercise.'),
    xmlNode('to', to?.join('; '), 'The state after the exercise.'),
  ], DEF_INTENDED_SHIFT);
}

export function xmlLifeMoment(id: LifeMomentIdT): XmlNodeT<'life_moment'> {
  const lm = LIFE_MOMENTS[id];
  return xmlNode('life_moment', [
    xmlNode('id', id),
    xmlNode('name', lm.name),
    xmlNode('description', lm.description),
    xmlNode('current_state', lm.shiftFrom.join('; ')),
    xmlNode('target_state', lm.shiftTo.join('; ')),
    xmlNode('ideal_environments', lm.idealEnvironments.join('; ')),
    xmlNode('ideal_duration', `${lm.idealDurationMin.join(' to ')} minutes`),
    xmlNode('ideal_modalities', lm.idealModalities.join('; ')),
  ]);
}

/**
 * Generates XML structure describing all valid life moments known
 * to the system.
 */
export function xmlValidLifeMoments(): XmlNodeT<'valid_life_moments'> {
  const nodes: Array<XmlNodeT<'life_moment'>> = [];
  for (const id of LIFE_MOMENT_IDS) nodes.push(xmlLifeMoment(id));

  return xmlNode(
    'valid_life_moments',
    nodes,
    'Valid life moments, known to the system',
  );
}

export function xmlModalities(modalities: string[]): XmlNodeT<'wellbeing_modalities'> {
  return xmlNode('wellbeing_modalities', modalities.join('; '), DEF_MODALITIES);
}

export function xmlNeurologicalMechanisms(
  mechanisms: string[] | undefined,
): XmlNodeT<'neurological_mechanisms'> | null {
  return xmlNode('neurological_mechanisms', mechanisms?.join('; '), {
    description: DEF_NEUROLOGICAL_MECHANISMS,
  });
}

export function xmlPhysicalMechanisms(
  mechanisms: string[] | undefined,
): XmlNodeT<'physical_mechanisms'> | null {
  return xmlNode('physical_mechanisms', mechanisms?.join('; '), {
    description: DEF_PHYSICAL_MECHANISMS,
  });
}

export function xmlTitle(title: null | string | undefined): XmlNodeT<'title'> | null {
  return xmlNode('title', title);
}

export function xmlTargetBodyAreas(areas: string[]): XmlNodeT<'target_body_areas'> {
  return xmlNode('target_body_areas', areas.join('; '));
}
